import { useEffect, useImperativeHandle, useRef } from 'react';

import { AppSettings } from '@/constants/app-settings';

let scriptLoadedPromise: Promise<unknown> | null = null;

async function loadChatbotScript() {
  if (scriptLoadedPromise) {
    return scriptLoadedPromise;
  }

  scriptLoadedPromise = new Promise<unknown>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = AppSettings.chatbotScriptUrl;
    script.type = 'module';
    script.async = true;

    // Promisify the script load event
    script.addEventListener('load', resolve);
    script.addEventListener('error', reject);
    document.head.append(script);
  });

  return scriptLoadedPromise;
}

export interface ChatbotWidgetApi {
  open: () => void;
  close: () => void;
}

export type ChatbotElement = HTMLElement & ChatbotWidgetApi;

export interface ChatbotWidgetProps {
  chatId: string;
  opened?: boolean;
  apiRef?: React.RefObject<ChatbotWidgetApi | null>;
}

/**
 * This component is used to load a widget with given ID
 * and append it to the body.
 *
 * The component makes sure that only one chat instance is available
 * on the page.
 */
export function ChatbotWidget({
  apiRef,
  chatId,
  opened = false,
}: ChatbotWidgetProps) {
  const chatbotRef = useRef<ChatbotElement>(null);

  // Define chatbot api methods
  useImperativeHandle(apiRef, () => {
    return {
      open: () => {
        chatbotRef.current?.open();
      },
      close: () => {
        chatbotRef.current?.close();
      },
    };
  }, []);

  // Create chatbot element and append it to the body
  useEffect(() => {
    loadChatbotScript().then(() => {
      // Remove any existing chatbot element
      const existingChatbot = document.querySelector('kanbu-chatbot');

      if (existingChatbot) {
        existingChatbot.remove();
      }

      // Create chatbot element and append it to the body
      const chatbotElement = document.createElement('kanbu-chatbot');
      chatbotElement.setAttribute('chat', chatId);

      if (opened) {
        chatbotElement.setAttribute('opened', 'true');
      }

      document.body.append(chatbotElement);
      chatbotRef.current = chatbotElement as ChatbotElement;
    });
  }, [chatId, opened]);

  return null;
}
